import React from 'react';
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Offcanvas from "react-bootstrap/Offcanvas";
import Container from "react-bootstrap/Container";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../store/root.reducer";
import User from "../../models/user.model";
import {LogOut} from "../auth/store/auth.actions";
import {useNavigate, useLocation} from "react-router-dom";
import styles from "./header.module.scss";
import Button from '../../components/molecules/Button';
import { NavDropdown } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { FTUEWrapper } from '../../components/ftue/FTUEWrapper';
import { canAccessArtworkWorkspace, getNormalizedUserRoles, hasAdminCapability, hasStrictAdminCapability } from "../../utils/userAccess";
import { useGalleryChromeContext } from "../../contexts/GalleryChromeContext";
import { getPseudoMoMReuseLegendItems } from "../../components/3d/utils/pseudoMomTagLight.utils";

export const Header = () => {

  const [ copied, setCopied ] = React.useState<boolean>(false);
  const [ copiedIframe, setCopiedIframe ] = React.useState<boolean>(false);

  // check logged in or not!
  const user: User = useSelector((state: AppState) => state.auth.user);
  const userRoles = getNormalizedUserRoles(user);
  const isAdminUser = hasAdminCapability(user);
  const isStrictAdminUser = hasStrictAdminCapability(user);
  const { isPseudoMoMGallery } = useGalleryChromeContext();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const onLogout = () => {
    dispatch(LogOut(navigate));
  };


  const routes = [
    {url: "/exhibitions/mom", text: 'Exhibitions', roles: []},
    {url: "/personal/mom", text: 'My Galleries', roles: ['SELLER']},
    {url: "/artwork/list", text: 'Artworks', roles: ['SELLER', 'GALLERY', 'ADMIN', 'SUPERADMIN'], access: 'artworks-workspace'},
    // {url: "/gallerysetup", text: "Setup gallery", roles: ['SELLER']},
    {url: "/admin/mom", text: 'Admin-MOM', roles: [], access: 'admin'},
    {url: "/admin/users", text: 'Users', roles: [], access: 'admin'},
    {url: "/admin/institutions", text: 'Institutions', roles: [], access: 'admin'},
    // {url: "/admin/system", text: 'System', roles: ADMIN_ROLES},
    {url: "/admin/galleries", text: 'Galleries', roles: [], access: 'admin'},
    {url: "/admin/gallery-labels", text: 'Gallery labels', roles: [], access: 'admin'},
    {url: "/admin/analytics", text: 'Analytics', roles: [], access: 'strict-admin'},
    {url: "/admin/backups", text: 'Legacy DB exports', roles: [], access: 'strict-admin'},
    {url: "/admin/assign-moods", text: 'Assign Moods', roles: [], access: 'admin'},
    {url: "/admin/feedbacks", text: 'Feedbacks', roles: [], access: 'admin'},
    {url: "/admin/spatial-migrations", text: 'Spatial Migrations', roles: [], access: 'strict-admin'},
    // {text: 'Files', roles: ADMIN_ROLES, items: [
    //   {url: "/admin/files/gallery", text: "Galleries"},
    //   {url: "/admin/files/artwork", text: "Artworks"},
    // ]},
  ]

  const successNotify = (msg: any) => {
    toast.success(msg, {
      position: "bottom-left",
    })
  }

  const accessibleRoutes = routes.filter((route: any) => {
    if (route.access === 'admin') {
      return isAdminUser;
    }
    if (route.access === 'artworks-workspace') {
      return canAccessArtworkWorkspace(user);
    }
    if (route.access === 'strict-admin') {
      return isStrictAdminUser;
    }

    return route.roles.length === 0 || (user && userRoles && route.roles.filter((r: string) => userRoles.includes(r)).length > 0);
  })

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href.replace(/\?.*$/, ''));
    successNotify("Gallery link has been copied");
  }

  const copyIframeCodeToClipboard = () => {
    let galleryURL = window.location.href.replace(/\?.*$/, '');
    galleryURL = galleryURL + "?mode=iframe";
    const iframeCode = `<iframe src="${galleryURL}" style="border:0px #ffffff none;" name="myiFrame" scrolling="no" frameborder="1" marginheight="0px" marginwidth="0px" height="720px" width="1280px" allowfullscreen></iframe>`
    navigator.clipboard.writeText(iframeCode);

    successNotify("Iframe code has been copied");
  }

  const [ isOffcanvasOpen, setIsOffcanvasOpen ] = React.useState<boolean>(false);
  const [ isShareOpen, setIsShareOpen ] = React.useState<boolean>(false);
  const [ isProfileOpen, setIsProfileOpen ] = React.useState<boolean>(false);
  const [ isAdminOpen, setIsAdminOpen ] = React.useState<boolean>(false);
  const shareRef = React.useRef<HTMLDivElement | null>(null);
  const profileRef = React.useRef<HTMLDivElement | null>(null);
  const adminRef = React.useRef<HTMLDivElement | null>(null);
  const pseudoMoMReuseLegendItems = React.useMemo(() => getPseudoMoMReuseLegendItems(), []);
  const showPseudoMoMReuseLegend = isPseudoMoMGallery && !isShareOpen && !isProfileOpen;
  const closeOffcanvas = () => setIsOffcanvasOpen(false);
  const navigateAndClose = (url: string) => {
    navigate(url);
    closeOffcanvas();
  }

  const renderPseudoMoMReuseLegend = (variant: "desktop" | "mobile") => (
    <div
      className={`${styles['reuse-legend']} ${styles[`reuse-legend-${variant}`]} user-select-none`}
      aria-label="Reuse colour legend"
    >
      <div className={styles['reuse-legend-title']}>Intended use</div>
      {pseudoMoMReuseLegendItems.map((item) => (
        <div className={styles['reuse-legend-row']} key={item.label}>
          <span
            className={styles['reuse-legend-dot']}
            style={{ backgroundColor: item.colorHex }}
            aria-hidden="true"
          />
          <span className={styles['reuse-legend-label']}>{item.label}</span>
        </div>
      ))}
    </div>
  );

  const adminRoutes = accessibleRoutes.filter(r => r.url.startsWith('/admin/'));
  const nonAdminRoutes = accessibleRoutes.filter(r => !r.url.startsWith('/admin/'));

  const isPathActive = (url: string) => {
    if (!url) return false;
    if (url === '/') return location.pathname === '/';
    return location.pathname === url || location.pathname.startsWith(url + '/');
  };
  const isAdminSectionActive = location.pathname.startsWith('/admin/');
  const [lastActiveUrl, setLastActiveUrl] = React.useState<string | 'admin' | null>(null);
  const hasAnyActive = isAdminSectionActive || nonAdminRoutes.some(r => isPathActive(r.url));

  React.useEffect(() => {
    if (isAdminSectionActive) {
      setLastActiveUrl('admin');
      return;
    }
    const matching = nonAdminRoutes
      .filter(r => isPathActive(r.url))
      .sort((a, b) => b.url.length - a.url.length); // prefer the longest match
    if (matching.length > 0) {
      setLastActiveUrl(matching[0].url);
    }
    // If nothing matches, intentionally keep previous lastActiveUrl
  }, [location.pathname, isAdminSectionActive, nonAdminRoutes]);

  React.useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (isShareOpen && shareRef.current && !shareRef.current.contains(targetNode)) {
        setIsShareOpen(false);
      }
      if (isProfileOpen && profileRef.current && !profileRef.current.contains(targetNode)) {
        setIsProfileOpen(false);
      }
      if (isAdminOpen && adminRef.current && !adminRef.current.contains(targetNode)) {
        setIsAdminOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };
  }, [isShareOpen, isProfileOpen, isAdminOpen]);

  return (
    <div className={styles.headerBackground}>
      <div className={styles['top-nav']}>
        <div className={styles['nav-left']}>
          <FTUEWrapper stepId="header-logo">
            <Navbar.Brand className={`${styles.headerBrand} user-select-none`} onClick={() => navigate('/')}> 
              <div className={styles['logo']}>
                <div className={styles['logo-dot']}></div>
                <div className={styles['logo-text']}>ARTETRA</div>
              </div>
            </Navbar.Brand>
          </FTUEWrapper>
          {nonAdminRoutes.map((item, key) => (
            <FTUEWrapper key={key} stepId={`header-nav-${item.text.toLowerCase().replace(/\s+/g, '-')}`}>
              <button
                className={`${styles['nav-button']} ${((hasAnyActive && isPathActive(item.url)) || (!hasAnyActive && lastActiveUrl === item.url)) ? styles['active'] : ''} user-select-none`}
                onClick={() => navigate(item.url)}
              >
                {item.text}
              </button>
            </FTUEWrapper>
          ))}
          {!!adminRoutes.length && (
            <div className={styles['admin-wrapper']} ref={adminRef}>
              <button
                className={`${styles['nav-button']} user-select-none ${((hasAnyActive && isAdminSectionActive) || (!hasAnyActive && lastActiveUrl === 'admin')) ? styles['active'] : ''}`}
                onClick={() => setIsAdminOpen(prev => !prev)}
              >
                Admin
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: 6 }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {isAdminOpen && (
                <div className={styles['profile-menu']}>
                  {adminRoutes.map((item) => (
                    <button
                      key={item.text}
                      className={`${styles['menu-option']} ${styles['profile-option']} user-select-none`}
                      onClick={() => { navigate(item.url); setIsAdminOpen(false); }}
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={styles['nav-right']}>
          <div className={styles['share-wrapper']} ref={shareRef}>
            <FTUEWrapper stepId="header-share-gallery">
              <button
                className={`${styles['btn']} ${styles['btn-primary']} user-select-none`}
                onClick={() => setIsShareOpen(prev => !prev)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="18" cy="5" r="3"></circle>
                  <circle cx="6" cy="12" r="3"></circle>
                  <circle cx="18" cy="19" r="3"></circle>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                </svg>
                SHARE GALLERY
              </button>
            </FTUEWrapper>
            {isShareOpen && (
              <div className={`${styles['profile-menu']} user-select-none`}>
                <button
                  className={`${styles['menu-option']} ${styles['profile-option']}`}
                  onClick={() => { copyToClipboard(); setIsShareOpen(false); }}
                >
                  Share link
                </button>
                <button
                  className={`${styles['menu-option']} ${styles['profile-option']}`}
                  onClick={() => { copyIframeCodeToClipboard(); setIsShareOpen(false); }}
                >
                  Generate Iframe Code
                </button>
              </div>
            )}
          </div>
          {user ? (
            <div className={styles['profile-dropdown-container']} ref={profileRef}>
              <button
                className={`${styles['profile-button']}`}
                onClick={() => setIsProfileOpen(prev => !prev)}
              >
                <span
                  className={styles['profile-text']}
                >
                  {user.name}
                </span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {isProfileOpen && (
                <div className={`${styles['profile-menu']} user-select-none`}>
                  <button
                    className={`${styles['menu-option']} ${styles['profile-option']} user-select-none`}
                    onClick={() => { navigate("/profile"); setIsProfileOpen(false); }}
                  >
                    Profile
                  </button>
                  <button
                    className={`${styles['menu-option']} ${styles['logout-option']} user-select-none`}
                    onClick={() => { onLogout(); setIsProfileOpen(false); }}
                  >
                    Log out
                  </button>
                </div>
              )}
              {showPseudoMoMReuseLegend && renderPseudoMoMReuseLegend("desktop")}
            </div>
          ) : (
            <div className={styles['profile-dropdown-container']} ref={profileRef}>
              <button
                className={`${styles['profile-button']} user-select-none`}
                onClick={() => setIsProfileOpen(prev => !prev)}
              >
                Profile
              </button>
              {isProfileOpen && (
                <div className={`${styles['profile-menu']} user-select-none`}>
                  <button
                    className={`${styles['menu-option']} ${styles['profile-option']} user-select-none`}
                    onClick={() => { navigate("/auth/login"); setIsProfileOpen(false); }}
                  >
                    Log in
                  </button>
                  <button
                    className={`${styles['menu-option']} ${styles['profile-option']} user-select-none`}
                    onClick={() => { navigate("/auth/register"); setIsProfileOpen(false); }}
                  >
                    Sign up
                  </button>
                </div>
              )}
              {showPseudoMoMReuseLegend && renderPseudoMoMReuseLegend("desktop")}
            </div>
          )}
        </div>
        <div className={styles['nav-toggle-wrapper']}>
          <FTUEWrapper stepId="header-hamburger-menu">
            <button 
              className={styles['nav-toggle']} 
              aria-label="Open menu" 
              onClick={() => setIsOffcanvasOpen(true)}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </FTUEWrapper>
          {showPseudoMoMReuseLegend && renderPseudoMoMReuseLegend("mobile")}
        </div>
        <Navbar.Offcanvas
          id="offcanvas-nav"
          aria-labelledby="offcanvas-nav-label"
          placement="start"
          className={`${styles.offcanvas} ${styles.offcanvasFull}`}
          show={isOffcanvasOpen}
          onHide={closeOffcanvas}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title id="offcanvas-nav-label" className={styles.headerBrand}>
              Menu
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Nav className="me-auto flex-column">
              {nonAdminRoutes.map((item, key) => (
                <Nav.Link key={key} active={isPathActive(item.url)} onClick={() => navigateAndClose(item.url)}>
                  {item.text}
                </Nav.Link>
              ))}
              {!!adminRoutes.length && adminRoutes.map((item) => (
                <Nav.Link key={item.text} active={isPathActive(item.url)} onClick={() => navigateAndClose(item.url)}>
                  {item.text}
                </Nav.Link>
              ))}
              <Nav.Link onClick={() => { copyToClipboard(); closeOffcanvas(); }}>
                Share link
              </Nav.Link>
              <Nav.Link onClick={() => { copyIframeCodeToClipboard(); closeOffcanvas(); }}>
                Generate Iframe Code
              </Nav.Link>
              {user ? (
                <>
                  <Nav.Link onClick={() => navigateAndClose("/profile")}>
                    Profile
                  </Nav.Link>
                  <Nav.Link onClick={() => { onLogout(); closeOffcanvas(); }}>
                    Log out
                  </Nav.Link>
                </>
              ) : (
                <>
                  <Nav.Link onClick={() => navigateAndClose("/auth/login")}>
                    Sign in
                  </Nav.Link>
                  <Nav.Link onClick={() => navigateAndClose("/auth/register")}>
                    Sign up
                  </Nav.Link>
                </>
              )}
            </Nav>
          </Offcanvas.Body>
        </Navbar.Offcanvas>
      </div>
      <ToastContainer />
    </div>
  );
};
